// cesium-events.ts - Manages Cesium event handling

import {
  ScreenSpaceEventType,
  Entity,
  Cartesian3,
  HeadingPitchRange,
} from "cesium";
import { tilesetRegistry } from "./cesium-assets";
import { applyStylesToAllTilesets } from "./cesium-styling";
import { flyToFeature } from "./cesium-camera";
import { setGisFeaturesOpacity } from "./cesium-gis";
import {
  onPolygonClick,
  onPolygonRightClick,
  startPolygonDrawing,
  stopPolygonDrawing,
  initializePolygonDrawing,
} from "./cesium-polygon";
import { changeImageryProvider } from "./cesium-imagery";

// Setup click event handlers
export function setupClickHandlers(
  viewer: any,
  onObjectClick?: (attributes: { [key: string]: any }) => void,
) {
  if (!onObjectClick) {
    // If no specific handler, still set up polygon interactions
    viewer.screenSpaceEventHandler.setInputAction((click: any) => {
      onPolygonClick(viewer, click);
    }, ScreenSpaceEventType.LEFT_CLICK);
    viewer.screenSpaceEventHandler.setInputAction(() => {
      onPolygonRightClick(viewer);
    }, ScreenSpaceEventType.RIGHT_CLICK);
    return;
  }

  viewer.screenSpaceEventHandler.setInputAction((click: any) => {
    try {
      // First, handle polygon drawing clicks
      onPolygonClick(viewer, click);

      // Then, handle object picking
      const pickedObject = viewer.scene.pick(click.position);
      if (pickedObject) {
        console.log("Picked object:", pickedObject);

        const handleFeatureClick = (properties: any, entity: any) => {
          if (!properties) return;
          setGisFeaturesOpacity(viewer, properties.ideaId);

          const url = properties.url || properties.link;
          if (url) {
            window.open(url, "_blank");
          }

          flyToFeature(viewer, entity);

          onObjectClick(properties);
        };

        if (pickedObject.id instanceof Entity && pickedObject.id.properties) {
          // Handle clicks on GIS feature entities
          const properties = pickedObject.id.properties.getValue(
            viewer.clock.currentTime
          );
          console.log("Clicked GIS Feature (Entity):", properties);
          handleFeatureClick(properties, pickedObject.id);
        } else if (
          pickedObject.primitive &&
          pickedObject.primitive.id instanceof Entity &&
          pickedObject.primitive.id.properties
        ) {
          // Handle clicks on primitives associated with an entity (e.g., points)
          const properties = pickedObject.primitive.id.properties.getValue(
            viewer.clock.currentTime
          );
          console.log("Clicked GIS Feature (Primitive):", properties);
          handleFeatureClick(properties, pickedObject.primitive.id);
        } else {
          console.log(
            "Picked object is not a known GIS feature, treating as 3D tileset feature."
          );
          // Handle clicks on 3D tileset features
          const feature = pickedObject as any;
          const content = feature._content;

          if (
            content &&
            content.batchTable &&
            content.batchTable._propertyTable
          ) {
            const propertyTable = content.batchTable._propertyTable;
            const metadataTable = propertyTable._metadataTable;

            if (
              metadataTable &&
              typeof metadataTable.getProperty === "function"
            ) {
              try {
                const propertyIds = metadataTable.getPropertyIds();
                const attributes: { [key: string]: any } = {};
                const batchId = feature._batchId;

                if (propertyIds && Array.isArray(propertyIds)) {
                  propertyIds.forEach((id: string) => {
                    try {
                      attributes[id] = metadataTable.getProperty(batchId, id);
                    } catch (propError) {
                    }
                  });
                  onObjectClick(attributes);
                }
              } catch (metadataError) {
              }
            }
          }
        }
      } else {
        // Clicked on globe, clear selection
        setGisFeaturesOpacity(viewer);
      }
    } catch (clickError) {
    }
  }, ScreenSpaceEventType.LEFT_CLICK);

  viewer.screenSpaceEventHandler.setInputAction(() => {
    onPolygonRightClick(viewer);
  }, ScreenSpaceEventType.RIGHT_CLICK);
}

// Setup global event listeners
export function setupGlobalEvents(viewer: any, isMobileDevice: boolean) {
  window.addEventListener("depthTestChange", ((event: CustomEvent) => {
    if (viewer) viewer.scene.globe.depthTestAgainstTerrain = event.detail.enabled;
  }) as EventListener);

  window.addEventListener("shadowsChange", ((event: CustomEvent) => {
    if (viewer) viewer.scene.globe.enableLighting = false; // Keep shadows off
  }) as EventListener);

  window.addEventListener("styleConditionsChange", ((event: CustomEvent) => {
    if (viewer) {
      applyStylesToAllTilesets(viewer, tilesetRegistry, event.detail.conditions);
    }
  }) as EventListener);

  window.addEventListener("imageryStyleChange", ((event: CustomEvent) => {
    if (viewer) {
      changeImageryProvider(viewer, event.detail.style, isMobileDevice).catch(
        (error) => {},
      );
    }
  }) as EventListener);

  window.addEventListener("polygonDrawingChange", ((event: CustomEvent) => {
    if (viewer) {
      if (event.detail.enabled) {
        initializePolygonDrawing(viewer);
        startPolygonDrawing(viewer);
      } else {
        stopPolygonDrawing(viewer);
      }
    }
  }) as EventListener);

  if (viewer && viewer.scene && viewer.scene.canvas) {
    viewer.scene.canvas.addEventListener(
      "webglcontextlost",
      (event: Event) => {
        event.preventDefault();
      },
      false,
    );
  }
} 